import { NavLink } from "react-router-dom";
import { X, ShieldCheck, PanelLeftClose, PanelLeftOpen } from "lucide-react";

import { cn } from "@/lib/utils";
import { NAV_SECTIONS, BOTTOM_ITEMS } from "@/config/navigation";
import { ASSETS, BRAND } from "@/config/brand";
import { useAuth } from "@/context/AuthContext";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { SHELL } from "@/constants/testIds";

const NavItem = ({ item, collapsed }) => {
  const Icon = item.icon;
  const link = (
    <NavLink
      to={item.path}
      end={item.path === "/"}
      data-testid={SHELL.navItem(item.key)}
      className={({ isActive }) =>
        cn(
          "group relative flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors",
          collapsed && "justify-center px-0",
          isActive
            ? "bg-white/10 text-white"
            : "text-white/70 hover:bg-white/5 hover:text-white",
        )
      }
    >
      {({ isActive }) => (
        <>
          {/* Restrained gold active indicator */}
          <span
            className={cn(
              "absolute left-0 top-1/2 h-5 w-[3px] -translate-y-1/2 rounded-r bg-brand-gold transition-opacity",
              isActive ? "opacity-100" : "opacity-0",
            )}
            aria-hidden
          />
          <Icon className={cn("h-[18px] w-[18px] shrink-0", isActive ? "text-brand-gold" : "text-white/70 group-hover:text-white")} />
          {!collapsed && <span className="truncate">{item.label}</span>}
        </>
      )}
    </NavLink>
  );

  if (!collapsed) return link;
  return (
    <Tooltip delayDuration={0}>
      <TooltipTrigger asChild>{link}</TooltipTrigger>
      <TooltipContent side="right">{item.label}</TooltipContent>
    </Tooltip>
  );
};

export const Sidebar = ({ open, onClose, collapsed, onToggleCollapse }) => {
  const { isAdmin } = useAuth();

  return (
    <>
      <div
        className={cn(
          "fixed inset-0 z-30 bg-brand-navy/50 backdrop-blur-sm transition-opacity lg:hidden",
          open ? "opacity-100" : "pointer-events-none opacity-0",
        )}
        onClick={onClose}
        aria-hidden
      />

      <TooltipProvider>
        <aside
          data-testid={SHELL.sidebar}
          data-collapsed={collapsed}
          className={cn(
            "fixed inset-y-0 left-0 z-40 flex flex-col bg-brand-navy text-white transition-[transform,width] duration-300 ease-out lg:static lg:z-auto lg:translate-x-0",
            collapsed ? "lg:w-[76px]" : "lg:w-72",
            "w-72",
            open ? "translate-x-0" : "-translate-x-full",
          )}
        >
          {/* Brand */}
          <div className={cn("flex items-center px-4 py-5", collapsed ? "justify-center" : "justify-between")}>
            {collapsed ? (
              <img src={ASSETS.mark} alt={BRAND.company} className="h-9 w-9" data-testid={SHELL.wordmark} />
            ) : (
              <div className="flex flex-col gap-1.5" data-testid={SHELL.wordmark} aria-label={`${BRAND.company} ${BRAND.product}`}>
                <img src={ASSETS.wordmark} alt={BRAND.company} className="h-6 w-auto" />
                <span className="pl-0.5 text-[10px] font-semibold uppercase tracking-[0.34em] text-brand-soft-gold">
                  {BRAND.product}
                </span>
              </div>
            )}
            <button
              onClick={onClose}
              className="rounded-md p-1.5 text-white/70 transition-colors hover:bg-white/10 hover:text-white lg:hidden"
              aria-label="Close navigation"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <div className="brand-hairline mx-4 h-px" />

          {/* Sections */}
          <nav className="flex-1 space-y-5 overflow-y-auto px-3 py-5" aria-label="Primary">
            {NAV_SECTIONS.map((section) => (
              <div key={section.id}>
                {!collapsed && section.id !== "home" && (
                  <p className="px-3 pb-2 text-[10px] font-semibold uppercase tracking-[0.24em] text-white/35">
                    {section.label}
                  </p>
                )}
                <div className="space-y-1">
                  {section.items.map((item) => (
                    <NavItem key={item.key} item={item} collapsed={collapsed} />
                  ))}
                </div>
              </div>
            ))}

            {isAdmin && (
              <div>
                {!collapsed && (
                  <p className="px-3 pb-2 text-[10px] font-semibold uppercase tracking-[0.24em] text-white/35">Platform</p>
                )}
                <NavItem item={{ key: "admin", label: "Admin", path: "/admin", icon: ShieldCheck }} collapsed={collapsed} />
              </div>
            )}
          </nav>

          {/* Bottom */}
          <div className="mt-auto space-y-1 px-3 pb-3">
            <div className="brand-hairline mb-3 h-px" />
            {BOTTOM_ITEMS.map((item) => (
              <NavItem key={item.key} item={item} collapsed={collapsed} />
            ))}
            <button
              onClick={onToggleCollapse}
              data-testid={SHELL.collapseToggle}
              className={cn(
                "mt-1 hidden w-full items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium text-white/60 transition-colors hover:bg-white/5 hover:text-white lg:flex",
                collapsed && "justify-center px-0",
              )}
              aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
            >
              {collapsed ? <PanelLeftOpen className="h-[18px] w-[18px]" /> : <><PanelLeftClose className="h-[18px] w-[18px]" /> Collapse</>}
            </button>
          </div>
        </aside>
      </TooltipProvider>
    </>
  );
};
